import React from "react";
import PublicNavbar from "../PublicNavbar";
import LegalFooter from "./LegalFooter";

const HealthWaiver = () => {
  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      <PublicNavbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">Health & Liability Waiver</h1>
          <p className="text-gray-400 mb-8">Last Updated: February 17, 2026</p>
          
          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            
            <section className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
              <h2 className="text-xl font-bold text-red-400 mb-2">IMPORTANT: Please Read Carefully</h2>
              <p className="text-gray-300 leading-relaxed">
                This Health & Liability Waiver ("Waiver") is a legal document that affects your rights. By using the Paladin Health & Human Performance platform, you acknowledge that you have read, understood, and agree to be bound by the terms of this Waiver. If you do not agree to these terms, do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Acknowledgment of Physical Activity Risks</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                I understand and acknowledge that:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Physical exercise and athletic training, including the activities provided through Paladin Health & Human Performance, involve inherent risks</li>
                <li>These risks include, but are not limited to: muscle strains, sprains, tears, and pulls; bone fractures and breaks; joint injuries; cardiovascular events including heart attack and stroke; dizziness, fainting, or loss of consciousness; aggravation of pre-existing conditions; and in extreme cases, permanent disability or death</li>
                <li>These risks exist regardless of the care and precautions taken by Paladin Health & Human Performance</li>
                <li>Certain exercises, particularly those involving high intensity, heavy weights, or explosive movements, carry elevated risks</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Voluntary Participation</h2>
              <p className="text-gray-300 leading-relaxed">
                I confirm that my participation in any training programs, workouts, or activities provided through Paladin Health & Human Performance is entirely voluntary. I am not required or obligated to perform any exercise or activity. I am free to modify, skip, or discontinue any exercise at any time based on my own judgment of my physical condition and capabilities.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Medical Clearance</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                I understand that Paladin Health & Human Performance strongly recommends that I:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Consult with a qualified physician before beginning any exercise program, especially if I have any pre-existing medical conditions, injuries, or health concerns</li>
                <li>Obtain medical clearance for physical activity from my healthcare provider</li>
                <li>Inform my physician about the types of activities I intend to perform</li>
                <li>Follow any exercise restrictions or modifications recommended by my healthcare provider</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                By using the Service, I represent that I have either obtained medical clearance or have made an informed decision to proceed without consulting a physician, and I accept full responsibility for that decision.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Assumption of Risk</h2>
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">I KNOWINGLY AND VOLUNTARILY ASSUME ALL RISKS</strong> associated with my participation in any training, exercise, or physical activity related to the Paladin Health & Human Performance platform, including but not limited to the risks described above. I assume these risks whether they arise from my own actions, the actions of others, the condition of any equipment I use, or any other cause. I understand that this assumption of risk includes risks that are known and unknown, foreseeable and unforeseeable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Release of Liability</h2>
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">I HEREBY RELEASE, WAIVE, DISCHARGE, AND COVENANT NOT TO SUE</strong> Paladin Health & Human Performance, its owners, officers, directors, employees, agents, affiliates, successors, and assigns (collectively, the "Released Parties") from any and all liability, claims, demands, actions, or causes of action arising out of or related to any loss, damage, injury, or death that I may sustain as a result of my participation in any training, exercise, or physical activity related to the Service, whether caused by the negligence of the Released Parties or otherwise.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Waiver of Negligence Claims</h2>
              <p className="text-gray-300 leading-relaxed">
                To the fullest extent permitted by applicable law, I waive any and all claims against the Released Parties based on negligence, including but not limited to claims arising from: the design or content of any training program; instructions, guidance, or recommendations provided through the Service; the failure to provide warnings or safety information; or any other act or omission that might otherwise give rise to a negligence claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Safe Training Environment and Equipment</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                I understand and agree that I am solely responsible for:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Ensuring I have a safe and appropriate environment in which to exercise</li>
                <li>Selecting, maintaining, and properly using any equipment required for my training</li>
                <li>Inspecting all equipment before use and discontinuing use of any defective equipment</li>
                <li>Using appropriate safety measures, including spotters when necessary</li>
                <li>Warming up adequately before exercise and cooling down afterward</li>
                <li>Staying hydrated and properly nourished</li>
                <li>Stopping any activity that causes pain, discomfort, or unusual symptoms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Emergency Responsibility</h2>
              <p className="text-gray-300 leading-relaxed">
                I understand that Paladin Health & Human Performance provides digital training content only and does not provide in-person supervision or emergency response services. In the event of an emergency, I am solely responsible for seeking appropriate medical attention. I will not hold Paladin Health & Human Performance responsible for any delays in receiving emergency care or for any consequences arising from emergency situations during my training.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. No Professional Supervision</h2>
              <p className="text-gray-300 leading-relaxed">
                I acknowledge that Paladin Health & Human Performance is a digital platform that provides training programs, educational content, and guidance. The Service does not include in-person supervision, personal training, medical monitoring, or real-time assessment of my form or technique. I am solely responsible for executing exercises safely and correctly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. Applicability</h2>
              <p className="text-gray-300 leading-relaxed">
                This Waiver applies to all use of the Paladin Health & Human Performance platform, including but not limited to: all training programs and workouts; educational content and guidance; AI-generated recommendations; performance analytics and tracking; and any other features or services provided through the platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Binding Effect</h2>
              <p className="text-gray-300 leading-relaxed">
                This Waiver is binding upon me and my heirs, executors, administrators, personal representatives, and assigns. I agree that if I bring any claim against the Released Parties in violation of this Waiver, I will be responsible for all costs, expenses, and attorney's fees incurred by the Released Parties in defending against such claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">12. Severability</h2>
              <p className="text-gray-300 leading-relaxed">
                If any provision of this Waiver is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. Any invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable while preserving its original intent.
              </p>
            </section>

            <section className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-blue-400 mb-4">Parental/Guardian Consent for Minors</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If the participant is under 18 years of age, a parent or legal guardian must read and agree to this Waiver on behalf of the minor. By allowing a minor to use the Paladin Health & Human Performance platform, the parent or legal guardian:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Acknowledges that they have read and understood this entire Waiver</li>
                <li>Agrees to be bound by all terms of this Waiver on behalf of the minor</li>
                <li>Assumes all risks associated with the minor's participation in training activities</li>
                <li>Releases and waives all claims on behalf of the minor as described above</li>
                <li>Agrees to supervise the minor's use of the Service and ensure safe participation</li>
                <li>Confirms that the minor has received medical clearance for physical activity (if applicable)</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                The parent or legal guardian understands that this Waiver is intended to be as broad and inclusive as permitted by applicable law, and agrees that if any portion is held invalid, the remainder shall continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">13. Acknowledgment</h2>
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">BY USING THE PALADIN HEALTH & HUMAN PERFORMANCE PLATFORM, I ACKNOWLEDGE THAT:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mt-4">
                <li>I have carefully read this Waiver and fully understand its contents</li>
                <li>I am aware that this Waiver releases Paladin Health & Human Performance from liability and waives certain legal rights I may have</li>
                <li>I am signing this Waiver freely and voluntarily, without any inducement or assurance of any nature</li>
                <li>I intend this Waiver to be a complete and unconditional release of all liability to the greatest extent allowed by law</li>
                <li>I am at least 18 years of age and legally competent to sign this Waiver, or I am a parent/guardian signing on behalf of a minor</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Contact Information</h2>
              <p className="text-gray-300 leading-relaxed">
                If you have questions about this Waiver, please contact us:
              </p>
              <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg border border-white/10">
                <p className="text-white font-semibold">Paladin Health & Human Performance</p>
                <p className="text-gray-400">Washington, United States</p>
                <p className="text-gray-400">Email: support@paladinhhp.com</p>
              </div>
            </section>

          </div>
        </div>
      </div>
      
      <LegalFooter />
    </div>
  );
};

export default HealthWaiver;
